import React, { useRef, useState, useContext, useEffect, Fragment } from 'react'
import * as Yup from 'yup'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { MapPinIcon } from '@heroicons/react/24/outline'
import { EventsAPI } from 'api'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import Header from 'components/Header'
import Datepicker from 'components/Datepicker'
import { emitter } from 'utils'
import { UserContext } from 'context/UserContext'
import GooglePlacesInput from 'components/Input/GooglePlaces'

export default function EventsAdd() {
    const formRef = useRef()
    const navigate = useNavigate()
    const { eventID } = useParams()
    const [loading, setLoading] = useState(true)
    const { memberID } = useParams
    const { state: userState } = useContext(UserContext)
    const [isMyEvent, setIsMyEvent] = useState(false)
    const [event, setEvent] = useState({})

    useEffect(() => {
        onEventDetails()
    }, [])

    const onEventDetails = async () => {
        try {
            setLoading(true)

            const { data } = await EventsAPI.getOne(eventID)

            if (data.success) {
                const { event } = data
                const { endingPoint, date, open, friends, description } = event
                const isMyEvent = event.user._id === userState.user._id

                if (!isMyEvent) return navigate(-1)

                setIsMyEvent(isMyEvent)
                setEvent(event)

                return formRef.current.setValues({
                    endingPoint,
                    date: dayjs(date).toDate(),
                    open,
                    friends,
                    description
                })
            }

            if (data.message === 'EVENT_DOESNT_EXIST') {
                navigate('/events/all')
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    const onUpdate = async ({
        endingPoint,
        date,
        open,
        friends,
        description
    }) => {
        const { data } = await EventsAPI.update(eventID, {
            endingPoint,
            date,
            open,
            friends,
            description
        })

        if (data.success) {
            emitter.emit('onOpenNotification', {
                type: 'success',
                title: 'Evento actualizado',
                message: `Tu evento se actualizó correctamente`
            })

            onBackward()
        }
    }

    const onBackward = () => {
        navigate(-1)
    }

    const initialValues = {
        endingPoint: '',
        date: dayjs().hour(0).minute(0).second(0).millisecond(0).toDate(),
        open: false,
        friends: false,
        description: ''
    }

    const validationSchema = Yup.object().shape({
        endingPoint: Yup.string().required('Este campo es requerido'),
        date: Yup.string().required('Este campo es requerido'),
        open: Yup.string().required('Este campo es requerido'),
        friends: Yup.string().required('Este campo es requerido'),
        description: Yup.string().required('Este campo es requerido')
    })

    return (
        <Fragment>
            <Header className="mb-12" onBackward={onBackward} />

            <div className="mb-10">
                <h1 className="font-bold mb-2 text-2xl text-white">
                    Actualizar evento
                </h1>
                <p>
                    Actualiza tu evento para que otros miembros puedan unirse a
                    tus rodadas.
                </p>
            </div>

            <Formik
                initialValues={initialValues}
                onSubmit={onUpdate}
                validationSchema={validationSchema}
                innerRef={formRef}>
                {({ errors, touched }) => (
                    <Form className="flex flex-col grow" autoComplete="off">
                        <div className="mb-auto flex flex-col gap-6">
                            <div>
                                <label
                                    htmlFor="endingPoint"
                                    className="inline-block text-sm mb-2 text-white">
                                    ¿Cuál es el lugar de destino?
                                </label>
                                <div className="relative">
                                    <MapPinIcon
                                        className={`w-5 absolute top-1/2 -translate-y-1/2 left-4 z-10 ${
                                            errors.endingPoint &&
                                            touched.endingPoint
                                                ? 'text-red'
                                                : 'text-text'
                                        }`}
                                    />
                                    <Field
                                        name="endingPoint"
                                        component={GooglePlacesInput}
                                        className={`w-full rounded-lg py-2 px-4 border pl-12 ${
                                            errors.endingPoint &&
                                            touched.endingPoint
                                                ? 'border-red focus:border-red bg-red/5 placeholder:text-red/50'
                                                : 'border-transparent focus:border-primary bg-dark-500 placeholder:text-text placeholder:text-sm'
                                        }`}
                                    />
                                </div>
                                <ErrorMessage
                                    name="endingPoint"
                                    component="small"
                                    className="text-red inline-block text-xs w-full mt-2"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="date"
                                    className="inline-block text-sm mb-2 text-white">
                                    Fecha del evento
                                </label>
                                <Field name="date" component={Datepicker} />
                                <ErrorMessage
                                    name="date"
                                    component="small"
                                    className="text-red inline-block text-xs w-full mt-2"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="open"
                                    className="inline-block text-sm mb-2 text-white">
                                    Evento abierto a:
                                </label>
                                <Field
                                    id="open"
                                    name="open"
                                    as="select"
                                    className={`w-full rounded-lg py-2 px-4 border ${
                                        errors.open && touched.open
                                            ? 'border-red focus:border-red bg-red/5 placeholder:text-red/50'
                                            : 'border-transparent focus:border-primary bg-dark-500 placeholder:text-text placeholder:text-sm'
                                    }`}>
                                    <option value={false}>
                                        Solo miembros de capítulo{' '}
                                        {userState.user.chapter}
                                    </option>
                                    <option value={true}>
                                        Todos los miembros del club
                                    </option>
                                </Field>
                                <ErrorMessage
                                    name="open"
                                    component="small"
                                    className="text-red inline-block text-xs w-full mt-2"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="friends"
                                    className="inline-block text-sm mb-2 text-white">
                                    ¿Se puede invitar amigos?
                                </label>
                                <Field
                                    id="friends"
                                    name="friends"
                                    as="select"
                                    className={`w-full rounded-lg py-2 px-4 border ${
                                        errors.friends && touched.friends
                                            ? 'border-red focus:border-red bg-red/5 placeholder:text-red/50'
                                            : 'border-transparent focus:border-primary bg-dark-500 placeholder:text-text placeholder:text-sm'
                                    }`}>
                                    <option value={false}>
                                        No, solo Coyotes
                                    </option>
                                    <option value={true}>
                                        Si, amigos del Club
                                    </option>
                                </Field>
                                <ErrorMessage
                                    name="friends"
                                    component="small"
                                    className="text-red inline-block text-xs w-full mt-2"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="description"
                                    className="inline-block text-sm mb-2 text-white">
                                    Detalles del evento
                                </label>
                                <Field
                                    id="description"
                                    name="description"
                                    as="textarea"
                                    rows="6"
                                    className={`w-full rounded-lg py-2 px-4 border ${
                                        errors.description &&
                                        touched.description
                                            ? 'border-red focus:border-red bg-red/5 placeholder:text-red/50'
                                            : 'border-transparent focus:border-primary bg-dark-500 placeholder:text-text placeholder:text-sm'
                                    }`}
                                />
                                <ErrorMessage
                                    name="description"
                                    component="small"
                                    className="text-red inline-block text-xs w-full mt-2"
                                />
                            </div>
                        </div>

                        <button
                            className="bg-primary font-bold text-white rounded-xl p-3 mt-10"
                            type="submit">
                            Actualizar evento
                        </button>
                    </Form>
                )}
            </Formik>
        </Fragment>
    )
}
